/**
 * Senior List View — re-based on the Delivery State Model.
 *
 * Route: /qa/list  (unchanged — QaListRoute in App.tsx wraps this)
 * SPEC:  ~/Productivity/QAM/foundation/delivery-state-model/SPEC-SeniorListview-UI.md
 * Data:  GET /api/qa/senior-list, /api/qa/senior-list/counts, /api/qa/senior-list/filters
 *
 * This file is a complete rebase of the previous starlingStatus-driven list.
 * It replaces:
 *   - /api/qa/deliveries fetch  →  /api/qa/senior-list
 *   - scoreboard tiles (Mismatch/Issues/Waiting)  →  Mismatch/Resolution Req./Needs QA
 *   - filter bar (driver-only)  →  4 multi-select dropdowns + sort + search
 *   - tab derivation (starlingStatus)  →  seniorState from payload
 *   - column set  →  10-column spec table
 *   - admin-only bulk skip  →  senior-level bulk skip/restore
 *
 * Removed (not in spec): bucket mode, freeze-for-baseline, cohort, PDR-check,
 *   bulk-import, per-row assign, admin-only controls.
 * Kept: import-batch progress bar (non-intrusive), WebSocket live updates.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useNotificationToasts, useNotificationPrefs, NOTIFICATION_PREFS_QUERY_KEY } from "@/hooks/use-notification-toasts";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/use-auth";
import { useQaWebSocket } from "@/hooks/use-websocket";
import { useLiveRefresh } from "@/lib/live-refresh";
import { Starling } from "@/lib/starling-brand";
import {
  parseListTab,
  buildListTabSearch,
  type ListTab,
} from "@/lib/qa-list-helpers";
import {
  Search,
  X,
  Loader2,
  Clock3,
  Wifi,
  WifiOff,
  Calendar,
  ArrowRight,
  AlertTriangle,
  AlertCircle,
  ChevronDown,
  SkipForward,
  Layers,
  RefreshCw,
  Bookmark,
  Check,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { normalizeFieldValue } from "@/lib/qa-detail-shared";
import { OPS_TZ } from "@/lib/ops-tz";
import { todayEtString } from "@/lib/ops-today";
import { writeSeniorListContext } from "@/lib/senior-list-context";
import {
  SENIOR_SEARCH_LIMIT,
  isSearchMode,
  searchStripText,
  formatCompletedAt,
  findActiveSavedFilter,
  isDefaultFilterState,
  buildSavedFilter,
  type SeniorFilterState,
} from "@/lib/senior-list-filters";
import {
  MAX_SAVED_FILTERS,
  DEFAULT_USER_PREFS,
  type SavedFilter,
  type UserPrefs,
} from "@shared/notification-preferences";
import { classifyField, normalizeMatchField, type FieldKind } from "@shared/match-state";
import { describeBagRule } from "@shared/bag-collection";
import { VIP_CATEGORIES, isVipCategory, vipCategoryFromRequirement, type VipCategory } from "@shared/vip";

// ─────────────────────────────────────────────────────────────────────────────
// Visual tokens (Delivery-QA blue identity, Senior purple, etc.)
// ─────────────────────────────────────────────────────────────────────────────

const V3 = {
  bg: "#F7F8FA",
  card: "#FFFFFF",
  sub: "#F3F4F6",
  good: "#15803D",
  goodBg: "#ECFDF5",
  bad: "#B91C1C",
  badBg: "#FEF2F2",
  amber: "#92400E",
  amberBg: "#FFFBEB",
  amberBorder: "#FDE68A",
  text: "#111827",
  textSecondary: "#6B7280",
  textMuted: "#9CA3AF",
  border: "#E5E7EB",
  accent: "#2563EB",       // Delivery-QA blue
  accentBg: "#DBEAFE",
};

const SR_PURPLE = Starling.purple;           // #592F96

/** Deterministic hue (0–359) from a user's display name. Stable per name. */
function nameToHue(name: string): number {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h) ^ name.charCodeAt(i);
    h = h >>> 0;
  }
  return h % 360;
}

/** Per-user badge colors: distinct HSL hue, fixed saturation/lightness for readability. */
function nameColor(name: string | null): { bg: string; text: string } {
  if (!name) return { bg: V3.sub, text: V3.textMuted };
  const hue = nameToHue(name);
  return {
    bg:   `hsl(${hue}, 55%, 91%)`,
    text: `hsl(${hue}, 60%, 28%)`,
  };
}

const TH_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: V3.textMuted,
  background: V3.sub,
  padding: "8px 8px",
  borderBottom: "1px solid " + V3.border,
};

const TD_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontFamily: Starling.fontBody,
  color: V3.text,
  padding: "10px 8px",
  verticalAlign: "top",
};

/** The next Sunday (ET) on or after today, as YYYY-MM-DD — the VIP import's default date. */
function nextSundayEtString(): string {
  const [y, m, d] = todayEtString().split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + ((7 - t.getUTCDay()) % 7));
  return t.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// Types — Data Contract §5.1
// ─────────────────────────────────────────────────────────────────────────────

interface SeniorListRow {
  id: string;
  onfleetId: string;
  seniorState: "needs-qa" | "in-progress" | "complete" | "skipped";
  type: "connect" | "b2b" | "regular" | "pickup";
  company: string;
  recipientName: string;
  address: string;
  isPickup: boolean;
  expectedActual: {
    street: [string | null, string | null];
    unit: [string | null, string | null];
    dz: [string | null, string | null];
  };
  attentionFlag: "mismatch" | "no_match" | "first_time" | "all_match" | null;
  /** E→A match bucket from the shared classifier; null = no comparable fields (pickups). */
  matchState: "mismatch" | "all-match" | "undetected" | null;
  /** VIP/priority flag text (qaRequirement) — non-null rows are pinned to the top server-side. */
  qaRequirement: string | null;
  driverName: string | null;
  driverZone: string | null;
  driverNotes: string | null;
  /** Did the completion note clearly say YB? null for non-CookUnity rows (SPEC-BagCollectionYB). */
  bagCollection: { value: "yb" | "not_yb"; rule: string } | null;
  seniorStatus: {
    decision: "good" | "bad" | null;
    resolution: "n/a" | "not-resolved" | "resolved" | null;
    claim: boolean;
    claimedBy: string | null;
  };
  agentStatus: {
    decision: "good" | "bad" | null;
    claim: boolean;
    ownerInitials: string | null;
    ownerName: string | null;
  };
  effectiveDecision: "good" | "bad" | null;
  resolutionNote: {
    body: string;
    author: string;
    authorRole: "agent" | "senior";
    when: string;
  } | null;
  completedAt: string | null;
  /** When the agent submitted their QA decision (ISO-8601 UTC); null until decided. */
  qaCompletedAt: string | null;
  seniorListviewSkippedAt: string | null;
  seniorSkippedBy: string | null;
}

interface SeniorListResponse {
  rows: SeniorListRow[];
  total: number;
  /** null in search mode (SPEC-2 §4.1 — the search ignores the date). */
  date: string | null;
  /** SPEC-2: true only for an all-days search; rows are capped at `limit`, `total` is the full count. */
  searchMode?: boolean;
  limit?: number;
}

interface SeniorListCounts {
  needs_qa: number;
  in_progress: number;
  complete: number;
  skipped: number;
  mismatch_in_needs_qa: number;
  resolution_required: number;
  /** Needs-QA rows with no agent AND no senior decision — nobody has looked yet. */
  needs_qa_unseen: number;
}

interface SeniorListFilters {
  drivers: Array<{ name: string; zone: string }>;
  owners: Array<{ name: string; initials: string }>;
  attention: string[];
  agentStatus: Array<string | null>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab mapping (seniorState → ListTab)
// ─────────────────────────────────────────────────────────────────────────────

// Map from URL tab param to API seniorState value
const TAB_TO_STATE: Record<ListTab, string> = {
  needsqa:    "needs-qa",
  inprogress: "in-progress",
  completed:  "complete",
  skipped:    "skipped",
};

const STATE_TO_TAB: Record<string, ListTab> = {
  "needs-qa":   "needsqa",
  "in-progress": "inprogress",
  "complete":   "completed",
  "skipped":    "skipped",
};

// ─────────────────────────────────────────────────────────────────────────────
// Small presentational helpers
// ─────────────────────────────────────────────────────────────────────────────

// Blue underline tab button matching the Delivery-QA identity
function ListTabButton({
  tab,
  label,
  count,
  active,
  onSelect,
  disabled = false,
}: {
  tab: ListTab;
  label: string;
  count: number;
  active: boolean;
  onSelect: (t: ListTab) => void;
  /** SPEC-2 §4.2 — dimmed and inert while an all-days search is active. */
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(tab)}
      disabled={disabled}
      data-testid={`tab-${tab}`}
      data-active={active || undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: "transparent",
        border: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        borderBottom: active ? "2px solid " + V3.accent : "2px solid transparent",
        color: active ? V3.accent : V3.textSecondary,
        fontFamily: Starling.fontHead,
        fontWeight: 600,
        fontSize: 14,
        padding: "10px 12px",
        marginBottom: -1,
      }}
    >
      {label}
      <span
        data-testid={`tab-count-${tab}`}
        style={{
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 999,
          padding: "1px 8px",
          background: active ? V3.accentBg : V3.sub,
          color: active ? V3.accent : V3.textMuted,
        }}
      >
        {count}
      </span>
    </button>
  );
}

function TabEmptyState({ copy, testid }: { copy: string; testid: string }) {
  return (
    <div
      className="text-center"
      style={{ padding: "40px 16px", color: V3.textMuted, fontSize: 14 }}
      data-testid={testid}
    >
      {copy}
    </div>
  );
}

// E→A match pill — replaces the Attention pill in the table (Sarah, 2026-08-19):
// shows the row's shared-classifier match bucket so the pill always agrees with
// the E→A field colors below the recipient. null (pickups / no fields) renders "—".
const MATCH_PILL_STYLE: Record<string, { bg: string; color: string }> = {
  "mismatch":   { bg: V3.badBg,   color: V3.bad },
  "undetected": { bg: V3.amberBg, color: V3.amber },
  "all-match":  { bg: Starling.goodCalmBg, color: Starling.goodCalm },
};
const MATCH_PILL_LABEL: Record<string, string> = {
  "mismatch":   "Mismatch",
  "undetected": "Undetected",
  "all-match":  "All Match",
};

function MatchPill({ state }: { state: string | null }) {
  if (!state) return <span style={{ color: V3.textMuted }}>—</span>;
  const s = MATCH_PILL_STYLE[state] ?? { bg: V3.sub, color: V3.textMuted };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        padding: "2px 10px",
        fontSize: 11,
        fontWeight: 700,
        background: s.bg,
        color: s.color,
      }}
      data-testid={`match-state-${state}`}
    >
      {MATCH_PILL_LABEL[state] ?? state}
    </span>
  );
}

// YB marker — a shopping-bags emoji before the driver note when the completion
// note clearly says the return bag was collected (SPEC-BagCollectionYB).
// Nothing at all is shown otherwise: a negative label read as "YB" at a glance
// (Sarah, 2026-09-23). The emoji is used nowhere else on this page. The tooltip
// comes from the same shared module the server's YB filter uses.
function YbMark({ bag }: { bag: SeniorListRow["bagCollection"] }) {
  if (bag?.value !== "yb") return null;
  return (
    <span
      title={describeBagRule(bag.rule)}
      aria-label={`YB: ${describeBagRule(bag.rule)}`}
      style={{ fontStyle: "normal", fontSize: 14, marginRight: 4, cursor: "help" }}
      data-testid="yb-mark"
    >
      🛍️
    </span>
  );
}

// VIP pill — rendered next to the recipient name when the row carries a
// qaRequirement flag (e.g. from the CookUnity VIP import). Tooltip = full text.
function VipPill({ requirement }: { requirement: string | null }) {
  if (!requirement) return null;
  const category = vipCategoryFromRequirement(requirement);
  return (
    <span
      title={requirement}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        borderRadius: 999,
        padding: "1px 8px",
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: 0.4,
        background: V3.amberBg,
        color: V3.amber,
        border: "1px solid " + V3.amberBorder,
        marginLeft: 6,
        verticalAlign: "middle",
      }}
      data-testid="vip-pill"
    >
      ⭐ VIP{category ? ` · ${category}` : ""}
    </span>
  );
}

// Senior status glyph — shows initials of the senior who decided/claimed.
// For decided rows (good/bad): initials from claimedBy (populated via seniorDecidedByName
// fallback in the backend). For active claim (no decision yet): initials (or 👀 if unknown).
// Badge color is per-user (deterministic HSL hash of the name).
function SrGlyph({ status }: { status: SeniorListRow["seniorStatus"] }) {
  const populated = !!(status.decision || status.claim);
  const initials = status.claimedBy
    ? status.claimedBy.split(" ").map((w: string) => w[0] ?? "").join("").toUpperCase().slice(0, 3)
    : null;
  const colors = nameColor(status.claimedBy);

  let glyph: React.ReactNode;
  if (status.claim && !status.decision) {
    glyph = initials ?? "👀";
  } else if (status.decision === "good") {
    glyph = initials ?? "✓";
  } else if (status.decision === "bad") {
    glyph = initials
      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><span style={{ fontSize: 10 }}>🔴</span><span style={{ fontSize: 9 }}>{initials}</span></span>
      : "🔴";
  } else {
    glyph = "—";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 26,
        height: 24,
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 700,
        background: populated ? colors.bg : V3.sub,
        color: populated ? colors.text : V3.textMuted,
      }}
      title={status.claimedBy
        ? status.decision
          ? `${status.decision === "good" ? "Good" : "Bad"} — ${status.claimedBy}`
          : `Claimed by ${status.claimedBy}`
        : undefined}
      data-testid="sr-glyph"
    >
      {glyph}
    </span>
  );
}

// Agent status sub-cell — shows 👀+initials for active undecided claim, initials for decided.
// Claim check comes BEFORE initials check so an active undecided claim always shows 👀.
// Badge color is per-user (deterministic HSL hash of ownerName).
function AgGlyph({ status }: { status: SeniorListRow["agentStatus"] }) {
  const populated = !!(status.decision || status.claim || status.ownerInitials);
  const colors = nameColor(status.ownerName);
  const isActiveClaim = status.claim && !status.decision;

  let glyph: React.ReactNode;
  if (isActiveClaim) {
    // Active undecided claim: 👀 + initials side by side
    glyph = status.ownerInitials
      ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
          <span style={{ fontSize: 12 }}>👀</span>
          <span style={{ fontSize: 9 }}>{status.ownerInitials}</span>
        </span>
      )
      : "👀";
  } else if (status.decision === "bad") {
    glyph = status.ownerInitials
      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><span style={{ fontSize: 10 }}>🔴</span><span style={{ fontSize: 9 }}>{status.ownerInitials}</span></span>
      : "🔴";
  } else if (status.decision === "good") {
    glyph = status.ownerInitials
      ? <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}><span style={{ fontSize: 9 }}>{status.ownerInitials}</span><span style={{ fontSize: 10 }}>✅</span></span>
      : "✅";
  } else if (status.ownerInitials) {
    glyph = status.ownerInitials;
  } else {
    glyph = "—";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: isActiveClaim ? 40 : 26,
        height: 24,
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        background: populated ? colors.bg : V3.sub,
        color: populated ? colors.text : V3.textMuted,
        letterSpacing: "0.02em",
        padding: isActiveClaim ? "0 4px" : 0,
      }}
      title={status.ownerName ?? undefined}
      data-testid="ag-glyph"
    >
      {glyph}
    </span>
  );
}

// Expected→Actual diff block
function ExpectedActualDiff({
  expectedActual,
  isPickup,
}: {
  expectedActual: SeniorListRow["expectedActual"];
  isPickup: boolean;
}) {
  if (isPickup) {
    return (
      <span style={{ fontSize: 11, fontStyle: "italic", color: V3.textMuted }}>
        Pickup task — barcode/tote reconciliation
      </span>
    );
  }

  // Match/mismatch decisions come from the shared classifier (@shared/match-state)
  // — the same one the server-side `match[]` filter uses — so the colors here and
  // the filter results can never disagree. Units compare via normalizeUnit:
  // "Suite 1110" → "1110" is a green match, not a red mismatch.
  const renderField = (label: string, kind: FieldKind, exp: string | null, act: string | null) => {
    const state = classifyField(kind, exp, act);
    if (state === "absent" && !exp && !act) return null;
    const noExtract = state === "undetected" && normalizeMatchField(kind, exp) !== "";
    const actColor = state === "match" ? V3.good : state === "mismatch" ? V3.bad : noExtract ? V3.amber : V3.textMuted;
    const actStyle: React.CSSProperties = noExtract
      ? { fontStyle: "italic", color: V3.amber }
      : { color: actColor };

    return (
      <div key={label} style={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>
        <span style={{ color: V3.textMuted }}>{label}</span>
        <span style={{ color: V3.textMuted }}>{exp ?? "—"}</span>
        <span style={{ color: V3.border }}>→</span>
        <span style={{ fontWeight: 600, ...actStyle }}>{act ?? "—"}</span>
      </div>
    );
  };

  const fields = [
    renderField("St:", "street", expectedActual.street[0], expectedActual.street[1]),
    renderField("Apt:", "unit", expectedActual.unit[0], expectedActual.unit[1]),
    renderField("DZ:", "dz", expectedActual.dz[0], expectedActual.dz[1]),
  ].filter(Boolean);

  if (fields.length === 0) return null;
  return <div style={{ marginTop: 3 }}>{fields}</div>;
}

// Resolution note cell
function ResolutionNoteCell({ note }: { note: SeniorListRow["resolutionNote"] }) {
  if (!note) return <span style={{ color: V3.textMuted }}>—</span>;

  const authorColor = note.authorRole === "agent" ? V3.accent : SR_PURPLE;
  const relTime = (() => {
    try {
      const ms = Date.now() - new Date(note.when).getTime();
      const m = Math.floor(ms / 60000);
      if (m < 60) return `${m}m ago`;
      const h = Math.floor(m / 60);
      if (h < 24) return `${h}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch {
      return "";
    }
  })();

  return (
    <div style={{ maxWidth: 104 }}>
      <div
        title={note.body}
        style={{
          fontSize: 12,
          color: V3.text,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {note.body}
      </div>
      <div style={{ fontSize: 10, marginTop: 2, display: "flex", gap: 4, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, color: authorColor }}>{note.author}</span>
        <span style={{ color: V3.textMuted }}>{relTime}</span>
      </div>
    </div>
  );
}

// Completed cell — day + time in OPS_TZ, no sort toggle on header. In search
// mode (SPEC-2 §4.2) rows span months, so the weekday is added:
// "Sun 20 Sep, 2:41 PM" instead of "Sep 20 2:41 PM".
function CompletedCell({ iso, searchMode = false }: { iso: string | null; searchMode?: boolean }) {
  const text = formatCompletedAt(iso, searchMode ? "search" : "day");
  if (!text) return <span style={{ color: V3.textMuted }}>—</span>;
  return (
    <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, whiteSpace: "nowrap", color: V3.textSecondary }}>
      {text}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Multi-select dropdown (agent status, owner, driver, attention)
// ─────────────────────────────────────────────────────────────────────────────

function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  renderOption,
  testid,
  disabled = false,
}: {
  label: string;
  options: string[];
  selected: Set<string>;
  onToggle: (v: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  renderOption?: (v: string) => React.ReactNode;
  testid?: string;
  /** SPEC-2 §4.2 — dimmed and inert while an all-days search is active. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // A disabled dropdown never stays open
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const summary = selected.size === 0 ? "All" : `${selected.size} selected`;

  return (
    <div ref={containerRef} style={{ position: "relative", opacity: disabled ? 0.45 : 1 }} data-testid={testid} data-disabled={disabled || undefined}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={disabled}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          padding: "5px 10px",
          borderRadius: 6,
          border: "1px solid " + V3.border,
          background: selected.size > 0 ? "#EEF2FF" : V3.card,
          color: selected.size > 0 ? "#4338CA" : V3.text,
          cursor: disabled ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
        }}
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span style={{ fontWeight: 600, fontSize: 10, color: selected.size > 0 ? "#6366F1" : V3.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {label}
        </span>
        <span>{summary}</span>
        <ChevronDown style={{ width: 12, height: 12, opacity: 0.6 }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 200,
            background: V3.card,
            border: "1px solid " + V3.border,
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            minWidth: 180,
            maxHeight: 280,
            overflowY: "auto",
          }}
          data-testid={testid ? `${testid}-panel` : undefined}
        >
          {/* Select all row */}
          <div
            style={{
              display: "flex",
              gap: 8,
              padding: "6px 10px",
              borderBottom: "1px solid " + V3.border,
            }}
          >
            <button
              type="button"
              onClick={() => { onSelectAll(); }}
              style={{ fontSize: 11, color: V3.accent, background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              Select all
            </button>
            <span style={{ color: V3.border }}>·</span>
            <button
              type="button"
              onClick={() => { onClearAll(); }}
              style={{ fontSize: 11, color: V3.accent, background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              Clear
            </button>
          </div>

          {options.map(opt => (
            <label
              key={opt}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 10px",
                cursor: "pointer",
                fontSize: 12,
                color: V3.text,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = V3.sub)}
              onMouseLeave={e => (e.currentTarget.style.background = "none")}
            >
              <input
                type="checkbox"
                checked={selected.has(opt)}
                onChange={() => onToggle(opt)}
                style={{ cursor: "pointer", accentColor: V3.accent }}
              />
              {renderOption ? renderOption(opt) : opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Senior List Table — 10 columns per spec §6
// ─────────────────────────────────────────────────────────────────────────────

function SeniorDeliveryTable({
  rows,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  searchMode = false,
}: {
  rows: SeniorListRow[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: (ids: string[], checked: boolean) => void;
  /** SPEC-2 §4.2 — rows span many days, so the completed cell shows the weekday too. */
  searchMode?: boolean;
}) {
  const allSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.id));
  const someSelected = rows.some(r => selectedIds.has(r.id));

  // Carry the list's full query string (date/tab/filters/sort) onto the detail
  // URL so the detail page's back-link (listUrl in qa-detail-v3) restores the
  // exact filtered view — including new-tab opens.
  const searchString = useSearch();
  const detailHref = (id: string) => searchString ? `/qa/delivery/${id}?${searchString}` : `/qa/delivery/${id}`;

  return (
    <div style={{ borderRadius: 6, overflow: "hidden" }}>
      <Table>
        <TableHeader>
          <TableRow>
            {/* Col 1 — checkbox */}
            <TableHead className="w-10" style={TH_STYLE}>
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={(checked) => onToggleSelectAll(rows.map(r => r.id), !!checked)}
                data-testid="checkbox-select-all"
              />
            </TableHead>
            {/* Col 2 — Status (Sr · Ag) */}
            <TableHead style={{ ...TH_STYLE, minWidth: 70 }}>Status<br /><span style={{ fontSize: 9, letterSpacing: "0.04em", fontWeight: 500 }}>SR · AG</span></TableHead>
            {/* Col 3 — Company */}
            {/* Column widths rebalanced 2026-09-23 (Sarah): driver notes are on
                nearly every delivery and the long ones matter; resolution notes are
                on ~8% and usually a few words. Company only needs "CookUnity". */}
            <TableHead style={{ ...TH_STYLE, width: 100 }}>Company</TableHead>
            {/* Col 4 — Recipient · E→A */}
            <TableHead style={{ ...TH_STYLE, minWidth: 180 }}>Recipient · E→A</TableHead>
            {/* Col 5 — Driver Notes */}
            <TableHead className="hidden lg:table-cell" style={{ ...TH_STYLE, width: 220 }}>Driver Notes</TableHead>
            {/* Col 6 — Driver */}
            <TableHead style={TH_STYLE}>Driver</TableHead>
            {/* Col 7 — E→A match (was Attention; replaced 2026-08-19) */}
            <TableHead style={TH_STYLE}>E→A</TableHead>
            {/* Col 8 — Resolution Notes */}
            <TableHead className="hidden md:table-cell" style={{ ...TH_STYLE, width: 120 }}>Resolution Notes</TableHead>
            {/* Col 9 — Delivery Completed (clock glyph; NO sort toggle) */}
            <TableHead className="hidden md:table-cell" style={{ ...TH_STYLE, textAlign: "right", whiteSpace: "nowrap" }} title="Delivery completed">
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                <Clock3 style={{ width: 11, height: 11 }} /> Delivery
              </span>
            </TableHead>
            {/* Col 10 — QA Completed (agent decision time, EST; clock glyph) */}
            <TableHead className="hidden md:table-cell" style={{ ...TH_STYLE, textAlign: "right", whiteSpace: "nowrap" }} title="QA completed (agent decision time)">
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}>
                <Clock3 style={{ width: 11, height: 11 }} /> QA
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.id}
              className="cursor-pointer hover-elevate"
              style={row.qaRequirement ? { background: V3.amberBg } : undefined}
              data-testid={`row-delivery-${row.id}`}
            >
              {/* Col 1 — checkbox (NOT a link: selection must not navigate) */}
              <TableCell onClick={e => e.stopPropagation()} style={{ ...TD_STYLE, paddingTop: 12 }}>
                <Checkbox
                  checked={selectedIds.has(row.id)}
                  onCheckedChange={() => onToggleSelect(row.id)}
                  data-testid={`checkbox-select-${row.id}`}
                />
              </TableCell>

              {/* Cols 2–10 wrap their content in a native <Link> (renders <a href>)
                  so the row supports cmd/ctrl+click, middle-click, and right-click →
                  "Open in new tab" — opening a delivery in a new tab without losing
                  the list's filters/day (which live in this tab's URL). Plain click
                  still navigates in-tab via wouter. Mirrors activetask-list.tsx. */}

              {/* Col 2 — Status (Sr · Ag) */}
              <TableCell style={TD_STYLE} data-testid={`status-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <SrGlyph status={row.seniorStatus} />
                    <AgGlyph status={row.agentStatus} />
                  </div>
                </Link>
              </TableCell>

              {/* Col 3 — Company */}
              <TableCell style={TD_STYLE} data-testid={`company-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  {row.isPickup
                    ? <span style={{ color: V3.textMuted, fontStyle: "italic" }}>Pickup</span>
                    : <span className="max-w-[84px] truncate block" title={row.company}>{row.company}</span>
                  }
                </Link>
              </TableCell>

              {/* Col 4 — Recipient · E→A */}
              <TableCell style={TD_STYLE} data-testid={`recipient-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {row.recipientName || "—"}
                    <VipPill requirement={row.qaRequirement} />
                  </div>
                  <div style={{ fontSize: 11, color: V3.textSecondary, marginTop: 1 }}>{row.address || ""}</div>
                  <ExpectedActualDiff expectedActual={row.expectedActual} isPickup={row.isPickup} />
                </Link>
              </TableCell>

              {/* Col 5 — Driver Notes */}
              <TableCell
                className="hidden lg:table-cell"
                style={{ ...TD_STYLE, color: V3.textSecondary, maxWidth: 220 }}
                data-testid={`driver-notes-${row.id}`}
              >
                <Link href={detailHref(row.id)} className="block">
                  {row.driverNotes
                    ? (
                      // Full note, wrapped (no 2-line cut-off — Sarah, 2026-09-23).
                      // overflowWrap breaks very long words/links instead of
                      // stretching the column.
                      <span style={{ fontSize: 12, fontStyle: "italic", overflowWrap: "anywhere" }}>
                        <YbMark bag={row.bagCollection} />
                        {row.driverNotes}
                      </span>
                    )
                    : <span style={{ color: V3.textMuted }}>—</span>
                  }
                </Link>
              </TableCell>

              {/* Col 6 — Driver */}
              <TableCell style={TD_STYLE} data-testid={`driver-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <div style={{ fontSize: 12, fontWeight: 500, color: V3.text }}>{row.driverName || "—"}</div>
                  {row.driverZone && (
                    <div style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", color: V3.textMuted }}>{row.driverZone}</div>
                  )}
                </Link>
              </TableCell>

              {/* Col 7 — Attention */}
              <TableCell style={TD_STYLE} data-testid={`attention-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <MatchPill state={row.matchState} />
                </Link>
              </TableCell>

              {/* Col 8 — Resolution Notes */}
              <TableCell className="hidden md:table-cell" style={TD_STYLE} data-testid={`resolution-note-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <ResolutionNoteCell note={row.resolutionNote} />
                </Link>
              </TableCell>

              {/* Col 9 — Delivery Completed */}
              <TableCell className="hidden md:table-cell" style={{ ...TD_STYLE, textAlign: "right" }} data-testid={`completed-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <CompletedCell iso={row.completedAt} searchMode={searchMode} />
                </Link>
              </TableCell>

              {/* Col 10 — QA Completed (agent decision time, EST) */}
              <TableCell className="hidden md:table-cell" style={{ ...TD_STYLE, textAlign: "right" }} data-testid={`qa-completed-${row.id}`}>
                <Link href={detailHref(row.id)} className="block">
                  <CompletedCell iso={row.qaCompletedAt} />
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Saved filters dropdown — SPEC-2 §3.2, Pipedrive-style
//
// One row per personal filter with a tick on the active one, inline rename
// (Enter saves, Esc cancels), delete (the page confirms), "Save current
// filters as…" with an inline name box, and a link to the Preferences page.
// ─────────────────────────────────────────────────────────────────────────────

function SavedFiltersDropdown({
  filters,
  activeSavedFilter,
  searchMode,
  canSaveCurrent,
  saveDisabledReason,
  onApply,
  onSave,
  onRename,
  onDelete,
}: {
  filters: SavedFilter[];
  activeSavedFilter: SavedFilter | null;
  searchMode: boolean;
  canSaveCurrent: boolean;
  saveDisabledReason: string | null;
  onApply: (f: SavedFilter) => void;
  /** Returns true when the save was issued (name accepted). */
  onSave: (name: string) => boolean;
  /** Returns true when the rename was issued (name accepted). */
  onRename: (id: string, name: string) => boolean;
  onDelete: (f: SavedFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Reset the inline editors whenever the menu closes
  useEffect(() => {
    if (open) return;
    setNaming(false);
    setNewName("");
    setRenamingId(null);
    setRenameValue("");
  }, [open]);

  const active = activeSavedFilter !== null;
  const iconBtn: React.CSSProperties = {
    background: "none", border: "none", cursor: "pointer", padding: 2, color: V3.textMuted, display: "inline-flex", borderRadius: 4,
  };
  const hint: React.CSSProperties = { fontSize: 10.5, color: V3.textMuted, marginTop: 2 };
  const separator: React.CSSProperties = { height: 1, background: V3.border, margin: "4px 0" };
  const inlineInput: React.CSSProperties = {
    flex: 1, minWidth: 0, fontSize: 12, padding: "4px 6px", border: "1px solid " + V3.accent, borderRadius: 4, outline: "none", fontFamily: Starling.fontBody, color: V3.text, background: V3.card,
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }} data-testid="saved-filters">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid="button-saved-filters"
        data-active={active || undefined}
        title={searchMode ? "Search is active — a filter applies once you clear it." : "Saved filters"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          padding: "6px 12px",
          borderRadius: 8,
          border: "1px solid " + (active ? V3.accent : V3.border),
          background: active ? V3.accentBg : V3.card,
          color: active ? V3.accent : V3.text,
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontFamily: Starling.fontBody,
        }}
      >
        <Bookmark style={{ width: 12, height: 12 }} />
        {activeSavedFilter ? `Filters: ${activeSavedFilter.name}` : "Filters"}
        <ChevronDown style={{ width: 12, height: 12, opacity: 0.6 }} />
      </button>

      {open && (
        <div
          data-testid="menu-saved-filters"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 200,
            background: V3.card,
            border: "1px solid " + V3.border,
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            minWidth: 280,
            maxWidth: 360,
            // 20 filters (the cap) must not run off the bottom of the viewport
            maxHeight: "min(70vh, 560px)",
            overflowY: "auto",
            padding: "6px 0",
          }}
        >
          <div style={{ padding: "4px 10px 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: V3.textMuted }}>
            MY SAVED FILTERS
          </div>

          {searchMode && (
            <div style={{ ...hint, padding: "0 10px 6px", color: V3.amber }} data-testid="saved-filters-search-hint">
              Search is active — applying a filter takes effect once you clear it.
            </div>
          )}

          {filters.length === 0 && (
            <div style={{ padding: "6px 10px 8px", fontSize: 12, color: V3.textMuted, fontStyle: "italic" }} data-testid="saved-filters-empty">
              No saved filters yet.
            </div>
          )}

          {filters.map(f => {
            const isActive = activeSavedFilter?.id === f.id;
            return (
              <div
                key={f.id}
                data-testid={`saved-filter-${f.id}`}
                data-active={isActive || undefined}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: isActive ? V3.accentBg : undefined }}
              >
                {renamingId === f.id ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === "Enter") { e.preventDefault(); if (onRename(f.id, renameValue)) setRenamingId(null); }
                      else if (e.key === "Escape") { e.preventDefault(); setRenamingId(null); }
                    }}
                    maxLength={60}
                    style={inlineInput}
                    data-testid={`input-rename-filter-${f.id}`}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => { onApply(f); setOpen(false); }}
                    data-testid={`button-apply-filter-${f.id}`}
                    style={{
                      flex: 1, minWidth: 0, textAlign: "left", background: "none", border: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 6, padding: 0,
                      fontSize: 12, fontFamily: Starling.fontBody, color: isActive ? V3.accent : V3.text, fontWeight: isActive ? 700 : 500,
                    }}
                  >
                    <span style={{ width: 12, display: "inline-flex", flexShrink: 0 }}>
                      {isActive && <Check style={{ width: 12, height: 12 }} data-testid={`saved-filter-active-${f.id}`} />}
                    </span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                  </button>
                )}
                <button type="button" title="Rename" onClick={() => { setRenamingId(f.id); setRenameValue(f.name); }} style={iconBtn} data-testid={`button-rename-filter-${f.id}`}>
                  <Pencil style={{ width: 12, height: 12 }} />
                </button>
                <button type="button" title="Delete" onClick={() => onDelete(f)} style={{ ...iconBtn, color: V3.bad }} data-testid={`button-delete-filter-${f.id}`}>
                  <Trash2 style={{ width: 12, height: 12 }} />
                </button>
              </div>
            );
          })}

          <div style={separator} />

          {naming ? (
            <div style={{ padding: "4px 10px 6px" }}>
              <input
                autoFocus
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") { e.preventDefault(); if (onSave(newName)) { setNaming(false); setNewName(""); } }
                  else if (e.key === "Escape") { e.preventDefault(); setNaming(false); setNewName(""); }
                }}
                maxLength={60}
                placeholder="Filter name"
                style={{ ...inlineInput, width: "100%" }}
                data-testid="input-new-filter-name"
              />
              <div style={hint}>Enter saves · Esc cancels · saves dropdowns + sort</div>
            </div>
          ) : (
            <div style={{ padding: "4px 10px 6px" }}>
              <button
                type="button"
                onClick={() => setNaming(true)}
                disabled={!canSaveCurrent}
                title={saveDisabledReason ?? "Save the current dropdowns + sort as a filter"}
                data-testid="button-save-current-filters"
                style={{
                  background: "none", border: "none", padding: 0, fontSize: 12, fontWeight: 600, fontFamily: Starling.fontBody,
                  color: canSaveCurrent ? V3.accent : V3.textMuted, cursor: canSaveCurrent ? "pointer" : "not-allowed",
                }}
              >
                + Save current filters as…
              </button>
              <div style={hint}>saves dropdowns + sort</div>
            </div>
          )}

          <div style={separator} />

          <Link href="/settings" data-testid="link-manage-filters" style={{ display: "block", padding: "4px 10px 2px", fontSize: 12, color: V3.accent, textDecoration: "none" }}>
            Manage in Preferences
          </Link>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────────────────

export default function QaList() {
  const { toast } = useToast();
  const { showToast } = useNotificationToasts();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { user } = useAuth();

  // ── Date picker ──────────────────────────────────────────────────────────

  const dateFromUrl = useMemo(() => {
    const p = new URLSearchParams(searchString);
    const d = p.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
  }, [searchString]);

  const [selectedDate, setSelectedDateState] = useState<string>(dateFromUrl || todayEtString());
  const isToday = selectedDate === todayEtString();

  const setSelectedDate = useCallback((date: string) => {
    setSelectedDateState(date);
    setSelectedIds(new Set());
    const p = new URLSearchParams(searchString);
    p.set("date", date);
    p.set("tab", parseListTab(searchString));
    setLocation(`/qa/list?${p.toString()}`, { replace: true });
  }, [searchString, setLocation]);

  // ── Tab ──────────────────────────────────────────────────────────────────

  const activeTab = useMemo(() => parseListTab(searchString), [searchString]);
  const setActiveTab = useCallback((tab: ListTab) => {
    setSelectedIds(new Set());
    const p = new URLSearchParams(searchString);
    p.set("tab", tab);
    setLocation(`/qa/list?${p.toString()}`, { replace: true });
  }, [searchString, setLocation]);

  // ── Filter state (persists across tab switches) ──────────────────────────
  // Seeded from the URL on mount and mirrored back on every change (below), so
  // a refresh, a detail-page back-link, or a bookmark restores the full view.

  const SORT_VALUES = ["completed_desc", "completed_asc", "qa_completed_desc", "qa_completed_asc"] as const;
  type SortValue = (typeof SORT_VALUES)[number];

  // Mount-only snapshot — later URL writes come from this state, not vice versa.
  const [initialUrlFilters] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const s = p.get("sort");
    return {
      agentStatus: p.getAll("agentStatus[]"),
      owner:       p.getAll("owner[]"),
      driver:      p.getAll("driver[]"),
      attention:   p.getAll("attention[]"),
      match:       p.getAll("match[]"),
      bag:         p.getAll("bag[]"),
      sort: (SORT_VALUES.includes(s as SortValue) ? s : "completed_asc") as SortValue,
      q: p.get("q") ?? "",
    };
  });

  const [agentStatusFilter, setAgentStatusFilter] = useState<Set<string>>(new Set(initialUrlFilters.agentStatus));
  const [ownerFilter, setOwnerFilter]             = useState<Set<string>>(new Set(initialUrlFilters.owner));
  const [driverFilter, setDriverFilter]           = useState<Set<string>>(new Set(initialUrlFilters.driver));
  const [attentionFilter, setAttentionFilter]     = useState<Set<string>>(new Set(initialUrlFilters.attention));
  const [matchFilter, setMatchFilter]             = useState<Set<string>>(new Set(initialUrlFilters.match));
  const [bagFilter, setBagFilter]                 = useState<Set<string>>(new Set(initialUrlFilters.bag));
  const [sort, setSort]                           = useState<SortValue>(initialUrlFilters.sort);
  const [searchQuery, setSearchQuery]             = useState(initialUrlFilters.q);
  const [debouncedSearch, setDebouncedSearch]     = useState(initialUrlFilters.q);

  // SPEC-2 §4.2 — search mode: text in the box means the server searches every
  // day and ignores date, tab, dropdowns and sort. The controls are disabled
  // (not cleared) so clearing the box restores exactly the previous view.
  const searchMode = isSearchMode(debouncedSearch);

  // Mirror filter/sort/search state into the URL (replace, not push — no history
  // spam while filtering). Date and tab are managed by their own setters; read
  // the live query string so we never clobber them.
  useEffect(() => {
    const current = new URLSearchParams(window.location.search);
    const next = new URLSearchParams(current);
    ["agentStatus[]", "owner[]", "driver[]", "attention[]", "match[]", "bag[]", "sort", "q"].forEach(k => next.delete(k));
    agentStatusFilter.forEach(v => next.append("agentStatus[]", v));
    ownerFilter.forEach(v => next.append("owner[]", v));
    driverFilter.forEach(v => next.append("driver[]", v));
    attentionFilter.forEach(v => next.append("attention[]", v));
    matchFilter.forEach(v => next.append("match[]", v));
    bagFilter.forEach(v => next.append("bag[]", v));
    if (sort !== "completed_asc") next.set("sort", sort);
    if (debouncedSearch.trim()) next.set("q", debouncedSearch.trim());
    if (next.toString() !== current.toString()) {
      setLocation(`/qa/list?${next.toString()}`, { replace: true });
    }
  }, [agentStatusFilter, ownerFilter, driverFilter, attentionFilter, matchFilter, bagFilter, sort, debouncedSearch, setLocation]);

  // Debounce search input
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback((v: string) => {
    setSearchQuery(v);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(v), 300);
  }, []);

  // ── Build query params for the API ──────────────────────────────────────

  const apiParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("date", selectedDate);
    p.set("tab", TAB_TO_STATE[activeTab]);
    p.set("sort", sort);
    if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
    agentStatusFilter.forEach(v => p.append("agentStatus[]", v));
    ownerFilter.forEach(v => p.append("owner[]", v));
    driverFilter.forEach(v => p.append("driver[]", v));
    attentionFilter.forEach(v => p.append("attention[]", v));
    matchFilter.forEach(v => p.append("match[]", v));
    bagFilter.forEach(v => p.append("bag[]", v));
    return p.toString();
  }, [selectedDate, activeTab, sort, debouncedSearch, agentStatusFilter, ownerFilter, driverFilter, attentionFilter, matchFilter, bagFilter]);

  // Mirror the current filter + sort into localStorage so the detail page's
  // senior auto-advance can honor it (the list's React state is gone once the
  // senior navigates into a delivery). Date is omitted — auto-advance is today-only.
  useEffect(() => {
    writeSeniorListContext({
      sort,
      q: debouncedSearch.trim() || undefined,
      agentStatus: Array.from(agentStatusFilter),
      owner: Array.from(ownerFilter),
      driver: Array.from(driverFilter),
      attention: Array.from(attentionFilter),
      match: Array.from(matchFilter),
      bag: Array.from(bagFilter),
    });
  }, [sort, debouncedSearch, agentStatusFilter, ownerFilter, driverFilter, attentionFilter, matchFilter, bagFilter]);

  // ── Saved filters (SPEC-2 §3) ────────────────────────────────────────────
  // Personal, stored on the user's preferences row (SPEC-1 §4); the whole list
  // is PATCHed on every create / rename / delete (§3.1). A filter captures the
  // five dropdowns + sort and never the date, the tab or the search text (D1).

  const { data: prefsData } = useNotificationPrefs();
  const prefs: UserPrefs = prefsData ?? DEFAULT_USER_PREFS;

  const savedFiltersMutation = useMutation({
    mutationFn: async (savedFilters: Array<SavedFilter | Omit<SavedFilter, "id">>) => {
      const res = await apiRequest("PATCH", "/api/user/notification-preferences", { savedFilters });
      return res.json() as Promise<UserPrefs>;
    },
    onSuccess: (serverPrefs) => {
      // The server assigns ids to new entries — take its copy of the row.
      queryClient.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, serverPrefs);
    },
    onError: (e: Error) => {
      toast({ title: "Couldn't save filters", description: e.message, variant: "destructive" });
    },
  });

  const currentFilterState: SeniorFilterState = useMemo(() => ({
    sort,
    agentStatus: Array.from(agentStatusFilter),
    owner: Array.from(ownerFilter),
    driver: Array.from(driverFilter),
    match: Array.from(matchFilter),
    bag: Array.from(bagFilter),
  }), [sort, agentStatusFilter, ownerFilter, driverFilter, matchFilter, bagFilter]);

  // "Filters: <name>" when the current state deep-equals a saved filter.
  // Clear filters resets the state, so it stops matching on its own.
  const activeSavedFilter = useMemo(
    () => findActiveSavedFilter(currentFilterState, prefs.savedFilters),
    [currentFilterState, prefs.savedFilters],
  );

  const applySavedFilter = (f: SavedFilter) => {
    // Only the five sets + sort. Date, tab and search are left alone, so the
    // change reaches the URL and qaSeniorListContext through the existing
    // mirror effects above and auto-advance honours it with no extra work.
    setAgentStatusFilter(new Set(f.agentStatus));
    setOwnerFilter(new Set(f.owner));
    setDriverFilter(new Set(f.driver));
    setMatchFilter(new Set(f.match));
    setBagFilter(new Set(f.bag));
    setSort(f.sort);
  };

  const nothingSelected = isDefaultFilterState(currentFilterState);
  const atFilterCap = prefs.savedFilters.length >= MAX_SAVED_FILTERS;
  const canSaveCurrent = !nothingSelected && !atFilterCap && !savedFiltersMutation.isPending;
  const saveDisabledReason: string | null = nothingSelected
    ? "Pick at least one dropdown value or a non-default sort first."
    : atFilterCap
      ? `You already have ${MAX_SAVED_FILTERS} saved filters — delete one first.`
      : null;

  const nameClashes = (name: string, exceptId?: string) =>
    prefs.savedFilters.some(f => f.id !== exceptId && f.name.toLowerCase() === name.toLowerCase());

  const saveCurrentFilters = (name: string): boolean => {
    const trimmed = name.trim();
    if (!trimmed || !canSaveCurrent) return false;
    if (nameClashes(trimmed)) {
      toast({ title: "Name already used", description: "Pick a different name for this filter.", variant: "destructive" });
      return false;
    }
    savedFiltersMutation.mutate([...prefs.savedFilters, buildSavedFilter(trimmed, currentFilterState)]);
    return true;
  };

  const renameSavedFilter = (id: string, name: string): boolean => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    if (nameClashes(trimmed, id)) {
      toast({ title: "Name already used", description: "Pick a different name for this filter.", variant: "destructive" });
      return false;
    }
    savedFiltersMutation.mutate(prefs.savedFilters.map(f => (f.id === id ? { ...f, name: trimmed } : f)));
    return true;
  };

  const [deleteTarget, setDeleteTarget] = useState<SavedFilter | null>(null);
  const deleteSavedFilter = (f: SavedFilter) => {
    savedFiltersMutation.mutate(prefs.savedFilters.filter(x => x.id !== f.id));
    setDeleteTarget(null);
  };

  // Same params minus tab (for /counts and /filters, which are date-scoped only)
  const dateOnlyParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("date", selectedDate);
    return p.toString();
  }, [selectedDate]);

  // ── API queries ──────────────────────────────────────────────────────────

  const { data: listData, isLoading: listLoading } = useQuery<SeniorListResponse>({
    queryKey: ["/api/qa/senior-list", apiParams],
    queryFn: async () => {
      const res = await fetch(`/api/qa/senior-list?${apiParams}`);
      if (!res.ok) throw new Error("Failed to fetch senior list");
      return res.json();
    },
  });

  const { data: counts } = useQuery<SeniorListCounts>({
    queryKey: ["/api/qa/senior-list/counts", dateOnlyParams],
    queryFn: async () => {
      const res = await fetch(`/api/qa/senior-list/counts?${dateOnlyParams}`);
      if (!res.ok) throw new Error("Failed to fetch counts");
      return res.json();
    },
  });

  const { data: filtersData } = useQuery<SeniorListFilters>({
    queryKey: ["/api/qa/senior-list/filters", dateOnlyParams],
    queryFn: async () => {
      const res = await fetch(`/api/qa/senior-list/filters?${dateOnlyParams}`);
      if (!res.ok) throw new Error("Failed to fetch filters");
      return res.json();
    },
  });

  // ── Derived tab counts from counts endpoint ──────────────────────────────

  const tabCounts: Record<ListTab, number> = useMemo(() => ({
    needsqa:    counts?.needs_qa    ?? 0,
    inprogress: counts?.in_progress ?? 0,
    completed:  counts?.complete    ?? 0,
    skipped:    counts?.skipped     ?? 0,
  }), [counts]);

  // ── Import batch progress (kept, non-intrusive) ──────────────────────────

  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const { data: importBatches } = useQuery<Array<{
    id: string; totalCount: number; pendingCount: number;
    completedCount: number; failedCount: number; duplicateCount: number;
    predictionsQueued: number; status: string; createdAt: string; completedAt: string | null;
  }>>({
    queryKey: ["/api/qa/bulk-import/status"],
    refetchInterval: activeBatchId ? 3000 : 30000,
  });

  const activeBatch = activeBatchId
    ? importBatches?.find(b => b.id === activeBatchId)
    : importBatches?.find(b => b.status !== "completed");

  useEffect(() => {
    if (activeBatch && !activeBatchId) setActiveBatchId(activeBatch.id);
    if (activeBatchId && !activeBatch && importBatches) {
      setActiveBatchId(null);
      queryClient.invalidateQueries({ predicate: q =>
        Array.isArray(q.queryKey) && q.queryKey[0] === "/api/qa/senior-list"
      });
    }
  }, [activeBatch?.id, activeBatchId, importBatches]);

  // ── Bulk skip / restore ──────────────────────────────────────────────────

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      checked ? ids.forEach(id => next.add(id)) : ids.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  const isSkippedTab = activeTab === "skipped";

  const skipMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const [first, ...rest] = ids;
      const res = await apiRequest(
        "POST",
        `/api/qa/deliveries/${first}/senior-listview-skip`,
        { ids: [first, ...rest] },
      );
      return res.json();
    },
    onSuccess: (_, ids) => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ predicate: q =>
        Array.isArray(q.queryKey) && (
          (q.queryKey[0] as string).startsWith("/api/qa/senior-list")
        )
      });
      showToast("actionConfirmation", {
        title: `Skipped ${ids.length}`,
        description: "Set aside on the Senior list. Agent queue unaffected.",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Skip failed", description: e.message, variant: "destructive" });
    },
  });

  const unskipMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const [first, ...rest] = ids;
      const res = await apiRequest(
        "POST",
        `/api/qa/deliveries/${first}/senior-listview-unskip`,
        { ids: [first, ...rest] },
      );
      return res.json();
    },
    onSuccess: (_, ids) => {
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ predicate: q =>
        Array.isArray(q.queryKey) && (
          (q.queryKey[0] as string).startsWith("/api/qa/senior-list")
        )
      });
      showToast("actionConfirmation", {
        title: `Restored ${ids.length}`,
        description: "Returned to Needs QA.",
      });
    },
    onError: (e: Error) => {
      toast({ title: "Restore failed", description: e.message, variant: "destructive" });
    },
  });

  const handleBulkAction = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (isSkippedTab) {
      unskipMutation.mutate(ids);
    } else {
      skipMutation.mutate(ids);
    }
  }, [selectedIds, isSkippedTab, skipMutation, unskipMutation]);

  // ── Refresh claims (PhaseD denorm) ──────────────────────────────────────

  const refreshClaimsMutation = useMutation({
    mutationFn: async () => {
      // NB: the seniorListview router is mounted under /api/qa (routes.ts) —
      // the bare /api/senior-list/... path was never mounted and 200s with HTML.
      const res = await apiRequest("POST", `/api/qa/senior-list/refresh-claims?date=${selectedDate}`);
      if (!res.ok) throw new Error("Refresh failed");
      return res.json() as Promise<{ ok: boolean; refreshed: number; date: string }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ predicate: q =>
        Array.isArray(q.queryKey) && (q.queryKey[0] as string).startsWith("/api/qa/senior-list")
      });
      showToast("actionConfirmation", { title: "Claims refreshed", description: `${data.refreshed} rows updated for ${data.date}.` });
    },
    onError: (e: Error) => {
      toast({ title: "Refresh failed", description: e.message, variant: "destructive" });
    },
  });

  // ── VIP import (CookUnity Sunday list) ──────────────────────────────────
  // Paste the raw Slack message; the server matches by recipient name against
  // the day's assigned QATT_1 tasks + completed rows, pins a "VIP — <reason>"
  // qaRequirement on each match, and returns a matched/unmatched/ambiguous
  // report rendered inside the dialog. Re-pasting is idempotent.
  const [vipDialogOpen, setVipDialogOpen] = useState(false);
  const [vipText, setVipText] = useState("");
  // Delivery date the pasted names are matched against. Defaults to the next
  // Sunday because CookUnity's list is for Sunday and lands days ahead — the
  // tasks already exist in Onfleet, so a future date works. Links/ids ignore it.
  const [vipDate, setVipDate] = useState<string>(nextSundayEtString);
  const [vipCategory, setVipCategory] = useState<VipCategory | "">("");
  const [vipReport, setVipReport] = useState<{
    flagged: Array<{ name: string; shortId: string; requirement: string; confidence: string }>;
    failed: Array<{ name: string; shortId: string; error: string }>;
    unmatched: string[];
    notFound: Array<{ name: string; ref: string; reason?: string }>;
    rejected: Array<{ name: string; ref: string; reason: string }>;
    ambiguous: Array<{ name: string; candidates: Array<{ shortId: string; recipientName: string }> }>;
    skippedLines: string[];
    candidateCount: number;
    date: string;
    category: string;
  } | null>(null);

  const vipImportMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/qa/vip-import", {
        text: vipText,
        date: vipDate,
        category: vipCategory,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setVipReport(data);
      queryClient.invalidateQueries({ predicate: q =>
        Array.isArray(q.queryKey) && (q.queryKey[0] as string).startsWith("/api/qa/senior-list")
      });
    },
    onError: (e: Error) => {
      toast({ title: "VIP import failed", description: e.message, variant: "destructive" });
    },
  });

  // ── Save as Cohort ───────────────────────────────────────────────────────
  const [cohortDialogOpen, setCohortDialogOpen] = useState(false);
  const [cohortName, setCohortName] = useState("");
  const [cohortDescription, setCohortDescription] = useState("");
  const [cohortQatt, setCohortQatt] = useState<"" | "1" | "3">("");
  const [cohortCompany, setCohortCompany] = useState("");

  const saveCohortMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/model-measurement/cohorts", {
        name: cohortName.trim(),
        description: cohortDescription.trim() || null,
        qatt: cohortQatt ? Number(cohortQatt) : null,
        company: cohortCompany.trim() || null,
        qaDeliveryIds: Array.from(selectedIds),
      });
      return res.json() as Promise<{ id: string; name: string; itemCount: number }>;
    },
    onSuccess: (data) => {
      setCohortDialogOpen(false);
      setCohortName("");
      setCohortDescription("");
      setCohortQatt("");
      setCohortCompany("");
      setSelectedIds(new Set());
      showToast("actionConfirmation", {
        title: `Cohort "${data.name}" saved`,
        description: `${data.itemCount} deliveries saved. View in Named Cohorts → Baseline Manager.`,
      });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const openSaveCohortDialog = useCallback(() => {
    if (selectedIds.size === 0) return;
    setCohortName("");
    setCohortDescription("");
    setCohortQatt("");
    setCohortCompany("");
    setCohortDialogOpen(true);
  }, [selectedIds]);

  // ── Start Next (senior /queue/next) ──────────────────────────────────────

  const [startNextLoading, setStartNextLoading] = useState(false);

  const handleStartNext = useCallback(async () => {
    if (!isToday) return;
    setStartNextLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("role", "senior");
      p.set("date", "today");
      p.set("sort", sort);
      if (debouncedSearch.trim()) p.set("q", debouncedSearch.trim());
      agentStatusFilter.forEach(v => p.append("agentStatus[]", v));
      ownerFilter.forEach(v => p.append("owner[]", v));
      driverFilter.forEach(v => p.append("driver[]", v));
      attentionFilter.forEach(v => p.append("attention[]", v));
      matchFilter.forEach(v => p.append("match[]", v));
      bagFilter.forEach(v => p.append("bag[]", v));
      const res = await fetch(`/api/qa/queue/next?${p.toString()}`);
      if (!res.ok) throw new Error("Queue request failed");
      const data = await res.json();
      // /queue/next returns { delivery: { id, ... } | null, claim }.
      const nextId = data?.delivery?.id ?? null;
      if (nextId) {
        setLocation(`/qa/delivery/${nextId}`);
      } else {
        toast({
          title: "All caught up",
          description: "Nothing in Needs QA within these filters.",
        });
      }
    } catch (e: any) {
      toast({ title: "Start Next failed", description: e.message, variant: "destructive" });
    } finally {
      setStartNextLoading(false);
    }
  }, [isToday, sort, debouncedSearch, agentStatusFilter, ownerFilter, driverFilter, attentionFilter, matchFilter, bagFilter, setLocation, toast]);

  // ── WebSocket live updates ────────────────────────────────────────────────

  // A1 + A12 (docs/incidents/2026-09-24-sunday-fixes-validation.md §5): the
  // list reloads its three queries at most once per 5 s (leading + trailing),
  // never while this tab is hidden, and once when the tab is shown again.
  // Before: three reloads on every live event, hidden tabs included — ~40 %
  // of all API calls in the busy minutes of Sunday 09-20.
  const refreshSeniorList = useLiveRefresh(() => {
    queryClient.invalidateQueries({ predicate: q =>
      Array.isArray(q.queryKey) && (
        (q.queryKey[0] as string).startsWith("/api/qa/senior-list")
      )
    });
  });

  const handleQaUpdate = useCallback((event: { type: string; delivery: any; claimedBy?: string }) => {
    refreshSeniorList();

    if (event.type === "new_delivery") {
      showToast("otherActivity", {
        title: "New Delivery",
        description: `${event.delivery.recipientName ?? "A delivery"} arrived`,
      });
    }
  }, [showToast, refreshSeniorList]);

  const { connectionState } = useQaWebSocket(
    user?.id ?? null,
    handleQaUpdate,
    { userName: user?.name || undefined, email: user?.email || undefined },
  );

  // ── Row navigation ───────────────────────────────────────────────────────
  // Rows navigate via native <Link> anchors in SeniorDeliveryTable (supports
  // open-in-new-tab); no imperative row-click handler needed.

  // ── Filter options ────────────────────────────────────────────────────────

  const driverOptions     = useMemo(() => filtersData?.drivers.map(d => d.name) ?? [], [filtersData]);
  const ownerOptions      = useMemo(() => filtersData?.owners.map(o => o.name) ?? [], [filtersData]);
  const agentStatusOptions = ["good", "bad", "—"];  // "—" = null/none
  const matchOptions = ["mismatch", "all-match", "undetected"];
  const bagOptions: string[] = ["yb", "not_yb"];  // shown as True / False

  const rows = listData?.rows ?? [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="mx-auto max-w-[1400px]"
      style={{ background: V3.bg, fontFamily: Starling.fontBody, color: V3.text, minHeight: "100vh" }}
      data-testid="senior-list-page"
    >
      <div
        className="flex flex-col gap-4"
        style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 32 }}
      >
        {/* ── Sticky header ─────────────────────────────────────────────── */}
        <div
          className="sticky top-0"
          style={{ zIndex: 40, background: V3.bg, paddingTop: 16, paddingBottom: 12, marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24 }}
        >
          {/* Page title + connection indicator */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <h1 style={{ fontFamily: Starling.fontHead, fontSize: 18, fontWeight: 700, color: V3.text, margin: 0 }}>
                Delivery QA
              </h1>
              <p style={{ fontSize: 12, color: V3.textMuted, margin: "2px 0 0" }}>Senior review firehose</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refreshClaimsMutation.mutate()}
                disabled={refreshClaimsMutation.isPending}
                title="Refresh claim display (syncs who's viewing)"
                style={{ color: V3.textMuted, padding: "4px 8px" }}
                data-testid="refresh-claims-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshClaimsMutation.isPending ? "animate-spin" : ""}`} />
              </Button>
              <div title={connectionState === "connected" ? "Real-time updates active" : "Connecting..."}>
                {connectionState === "connected"
                  ? <Wifi className="h-4 w-4" style={{ color: V3.good }} />
                  : <WifiOff className="h-4 w-4 animate-pulse" style={{ color: V3.textMuted }} />
                }
              </div>
            </div>
          </div>

          {/* Scoreboard — 3 cards, global counts */}
          <div
            style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}
            data-testid="scoreboard"
          >
            {/* Mismatch — red */}
            <div
              style={{
                background: V3.badBg,
                border: "1px solid #FECACA",
                borderRadius: 10,
                padding: "12px 18px",
                minWidth: 140,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              data-testid="scorecard-mismatch"
            >
              <div style={{ fontFamily: Starling.fontHead, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: V3.bad, marginBottom: 4 }}>Mismatch</div>
              <div style={{ fontFamily: Starling.fontHead, fontSize: 30, fontWeight: 800, color: V3.bad, lineHeight: 1 }} data-testid="count-mismatch">
                {counts?.mismatch_in_needs_qa ?? "—"}
              </div>
            </div>

            {/* Resolution Req. — amber */}
            <div
              style={{
                background: V3.amberBg,
                border: "1px solid " + V3.amberBorder,
                borderRadius: 10,
                padding: "12px 18px",
                minWidth: 140,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              data-testid="scorecard-resolution"
            >
              <div style={{ fontFamily: Starling.fontHead, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: V3.amber, marginBottom: 4 }}>Resolution Req.</div>
              <div style={{ fontFamily: Starling.fontHead, fontSize: 30, fontWeight: 800, color: V3.amber, lineHeight: 1 }} data-testid="count-resolution">
                {counts?.resolution_required ?? "—"}
              </div>
            </div>

            {/* Needs QA — ink */}
            <div
              style={{
                background: V3.card,
                border: "1px solid " + V3.border,
                borderRadius: 10,
                padding: "12px 18px",
                minWidth: 140,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              }}
              data-testid="scorecard-needs-qa"
            >
              <div style={{ fontFamily: Starling.fontHead, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: V3.textMuted, marginBottom: 4 }}>Needs QA</div>
              <div style={{ fontFamily: Starling.fontHead, fontSize: 30, fontWeight: 800, color: V3.text, lineHeight: 1 }} data-testid="count-needs-qa">
                {counts?.needs_qa ?? "—"}
              </div>
              {counts != null && (
                <div style={{ fontFamily: Starling.fontBody, fontSize: 10.5, fontWeight: 600, color: V3.textMuted, marginTop: 4, whiteSpace: "nowrap" }} data-testid="count-needs-qa-split">
                  {counts.needs_qa_unseen} unseen · {Math.max(0, counts.needs_qa - counts.needs_qa_unseen)} agent-decided
                </div>
              )}
            </div>
          </div>

          {/* Filter bar — one row */}
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            data-testid="filter-bar"
          >
            {/* Date picker — inert while searching (SPEC-2 §4.2) */}
            <div
              style={{ display: "flex", alignItems: "center", gap: 6, background: V3.card, border: "1px solid " + V3.border, borderRadius: 8, padding: "5px 10px", opacity: searchMode ? 0.45 : 1 }}
              title={searchMode ? "Date is ignored while searching." : undefined}
            >
              <Calendar style={{ width: 14, height: 14, color: V3.textMuted }} />
              <input
                type="date"
                value={selectedDate}
                max={todayEtString()}
                onChange={e => setSelectedDate(e.target.value)}
                disabled={searchMode}
                style={{ border: "none", background: "transparent", outline: "none", fontSize: 12, color: V3.text, cursor: searchMode ? "not-allowed" : "pointer" }}
                data-testid="input-date-picker"
              />
              {isToday
                ? <span style={{ fontSize: 10, fontWeight: 800, color: V3.good, background: V3.goodBg, padding: "1px 7px", borderRadius: 999 }}>Today</span>
                : <span style={{ fontSize: 10, fontWeight: 800, color: V3.amber, background: V3.amberBg, padding: "1px 7px", borderRadius: 999 }}>Past date</span>
              }
            </div>

            {/* Start Next */}
            <div
              style={{ position: "relative", display: "inline-flex", opacity: searchMode ? 0.45 : 1 }}
              title={searchMode ? "Clear the search to use Get next." : !isToday ? "Auto-advance only serves today's queue." : undefined}
            >
              <button
                type="button"
                onClick={handleStartNext}
                disabled={!isToday || startNextLoading || searchMode}
                data-testid="button-start-next"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  background: isToday && !searchMode ? V3.accent : "#C3CEE4",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: 8,
                  padding: "7px 14px",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: isToday && !searchMode ? "pointer" : "not-allowed",
                  fontFamily: Starling.fontBody,
                }}
              >
                {startNextLoading
                  ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                  : <ArrowRight style={{ width: 14, height: 14 }} />
                }
                Start next
              </button>
            </div>

            {/* Agent Status multi-select */}
            <MultiSelectDropdown
              label="Agent Status"
              options={agentStatusOptions}
              selected={agentStatusFilter}
              onToggle={v => setAgentStatusFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
              onSelectAll={() => setAgentStatusFilter(new Set(agentStatusOptions))}
              onClearAll={() => setAgentStatusFilter(new Set())}
              renderOption={v => <span>{v === "—" ? "— (none)" : v === "good" ? "✓ Good" : "🔴 Bad"}</span>}
              testid="filter-agent-status"
              disabled={searchMode}
            />

            {/* Agent Owner multi-select */}
            <MultiSelectDropdown
              label="Agent Owner"
              options={ownerOptions}
              selected={ownerFilter}
              onToggle={v => setOwnerFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
              onSelectAll={() => setOwnerFilter(new Set(ownerOptions))}
              onClearAll={() => setOwnerFilter(new Set())}
              testid="filter-owner"
              disabled={searchMode}
            />

            {/* Driver multi-select */}
            <MultiSelectDropdown
              label="Driver"
              options={driverOptions}
              selected={driverFilter}
              onToggle={v => setDriverFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
              onSelectAll={() => setDriverFilter(new Set(driverOptions))}
              onClearAll={() => setDriverFilter(new Set())}
              testid="filter-driver"
              disabled={searchMode}
            />

            {/* Attention dropdown removed 2026-08-19 — superseded by the E→A
                match filter below. attention[] plumbing is kept server-side and
                in the URL mirror so old bookmarks still work. */}

            {/* E→A match multi-select — buckets from the shared match-state
                classifier (any red / all green / one+ side missing) */}
            <MultiSelectDropdown
              label="E→A"
              options={matchOptions}
              selected={matchFilter}
              onToggle={v => setMatchFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
              onSelectAll={() => setMatchFilter(new Set(matchOptions))}
              onClearAll={() => setMatchFilter(new Set())}
              renderOption={v => <MatchPill state={v} />}
              testid="filter-match"
              disabled={searchMode}
            />

            {/* YB multi-select — did the driver's completion note clearly say
                YB? True / False (CookUnity rows only; SPEC-BagCollectionYB) */}
            <MultiSelectDropdown
              label="YB"
              options={bagOptions}
              selected={bagFilter}
              onToggle={v => setBagFilter(prev => { const n = new Set(prev); n.has(v) ? n.delete(v) : n.add(v); return n; })}
              onSelectAll={() => setBagFilter(new Set(bagOptions))}
              onClearAll={() => setBagFilter(new Set())}
              renderOption={v => <span>{v === "yb" ? "True" : "False"}</span>}
              testid="filter-bag"
              disabled={searchMode}
            />

            {/* Sort — ignored by the server in search mode (SPEC-2 §4.1) */}
            <select
              value={sort}
              onChange={e => setSort(e.target.value as typeof sort)}
              disabled={searchMode}
              data-testid="select-sort"
              style={{
                fontSize: 12,
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid " + V3.border,
                background: V3.card,
                color: V3.text,
                cursor: searchMode ? "not-allowed" : "pointer",
                opacity: searchMode ? 0.45 : 1,
              }}
            >
              <option value="completed_desc">Delivery Completed · Newest first</option>
              <option value="completed_asc">Delivery Completed · Oldest first</option>
              <option value="qa_completed_desc">QA Completed · Newest first</option>
              <option value="qa_completed_asc">QA Completed · Oldest first</option>
            </select>

            {/* Search */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 200, maxWidth: 320, background: V3.card, border: "1px solid " + V3.border, borderRadius: 8, padding: "5px 10px" }}>
              <Search style={{ width: 14, height: 14, color: V3.textMuted, flexShrink: 0 }} />
              <input
                type="text"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="OnFleet ID / barcode / recipient / address"
                style={{ border: "none", background: "transparent", outline: "none", fontSize: 12, color: V3.text, flex: 1, minWidth: 0 }}
                data-testid="input-search"
              />
              {searchQuery && (
                <button type="button" onClick={() => { handleSearchChange(""); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: V3.textMuted }}>
                  <X style={{ width: 12, height: 12 }} />
                </button>
              )}
            </div>

            {/* Clear all filters — resets every filter, search, and sort to
                defaults (URL params clear via the mirror effect). Hidden when
                nothing is active. Date and tab are deliberately kept. */}
            {(agentStatusFilter.size > 0 || ownerFilter.size > 0 || driverFilter.size > 0 || attentionFilter.size > 0 || matchFilter.size > 0 || bagFilter.size > 0 || searchQuery !== "" || sort !== "completed_asc") && (
              <button
                type="button"
                onClick={() => {
                  setAgentStatusFilter(new Set());
                  setOwnerFilter(new Set());
                  setDriverFilter(new Set());
                  setAttentionFilter(new Set());
                  setMatchFilter(new Set());
                  setBagFilter(new Set());
                  setSort("completed_asc");
                  handleSearchChange("");
                }}
                data-testid="button-clear-filters"
                title="Reset all filters, search, and sort"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  background: V3.card,
                  color: V3.bad,
                  border: "1px solid #FECACA",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: Starling.fontBody,
                  whiteSpace: "nowrap",
                }}
              >
                <X style={{ width: 12, height: 12 }} />
                Clear filters
              </button>
            )}

            {/* Saved filters — SPEC-2 §3.2. Stays enabled while searching;
                applying one only takes effect once the search is cleared. */}
            <SavedFiltersDropdown
              filters={prefs.savedFilters}
              activeSavedFilter={activeSavedFilter}
              searchMode={searchMode}
              canSaveCurrent={canSaveCurrent}
              saveDisabledReason={saveDisabledReason}
              onApply={applySavedFilter}
              onSave={saveCurrentFilters}
              onRename={renameSavedFilter}
              onDelete={f => setDeleteTarget(f)}
            />

            {/* VIP import — paste CookUnity's Sunday Slack list */}
            <button
              type="button"
              onClick={() => { setVipReport(null); setVipDialogOpen(true); }}
              data-testid="button-vip-import"
              title="Paste CookUnity's VIP list to flag deliveries"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: V3.amberBg,
                color: V3.amber,
                border: "1px solid " + V3.amberBorder,
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: Starling.fontBody,
                whiteSpace: "nowrap",
              }}
            >
              ⭐ VIP Import
            </button>
          </div>

          <div style={{ height: 1, background: V3.border, marginLeft: -24, marginRight: -24, marginTop: 12 }} />
        </div>

        {/* ── Import batch progress ──────────────────────────────────────── */}
        {activeBatch && (
          <div className="rounded-md" style={{ background: V3.card, border: "1px solid " + V3.border, padding: 12 }} data-testid="card-import-progress">
            <div className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" style={{ color: V3.accent }} />
                <span style={{ fontWeight: 500 }}>Importing deliveries…</span>
                <span style={{ color: V3.textSecondary }}>
                  {activeBatch.completedCount + activeBatch.failedCount + activeBatch.duplicateCount} / {activeBatch.totalCount}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* ── Tabs — dimmed + inert while searching; counts stay on the selected date ── */}
        <div
          style={{ display: "flex", gap: 4, borderBottom: "1px solid " + V3.border, opacity: searchMode ? 0.45 : 1 }}
          data-testid="qa-list-tabs"
          title={searchMode ? "Tab is ignored while searching." : undefined}
        >
          <ListTabButton tab="needsqa"    label="Needs QA"    count={tabCounts.needsqa}    active={activeTab === "needsqa"}    onSelect={setActiveTab} disabled={searchMode} />
          <ListTabButton tab="inprogress" label="In Progress" count={tabCounts.inprogress} active={activeTab === "inprogress"} onSelect={setActiveTab} disabled={searchMode} />
          <ListTabButton tab="completed"  label="Completed"   count={tabCounts.completed}  active={activeTab === "completed"}  onSelect={setActiveTab} disabled={searchMode} />
          <ListTabButton tab="skipped"    label="Skipped"     count={tabCounts.skipped}    active={activeTab === "skipped"}    onSelect={setActiveTab} disabled={searchMode} />
        </div>

        {/* ── Search-mode strip (SPEC-2 §4.2) ─────────────────────────────── */}
        {searchMode && (
          <div
            data-testid="search-mode-strip"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: V3.accentBg,
              border: "1px solid #BFDBFE",
              borderRadius: 6,
              fontSize: 12.5,
              color: "#1E3A8A",
              fontWeight: 500,
            }}
          >
            <Search style={{ width: 14, height: 14, flexShrink: 0 }} />
            <span>
              {listData?.searchMode
                ? searchStripText(debouncedSearch, listData.total, listData.limit ?? SENIOR_SEARCH_LIMIT)
                : `Searching all days for "${debouncedSearch.trim()}"…`}
            </span>
          </div>
        )}

        {/* ── Bulk action bar (above column headers) ──────────────────── */}
        {rows.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 12px",
              background: selectedIds.size > 0 ? "#F3F0F9" : V3.sub,
              border: "1px solid " + V3.border,
              borderRadius: 6,
              fontSize: 12.5,
            }}
            data-testid="bulk-action-bar"
          >
            <span style={{ fontWeight: 700, color: V3.textMuted }} data-testid="bulk-selected-count">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select rows to bulk skip"}
            </span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={openSaveCohortDialog}
              disabled={selectedIds.size === 0 || saveCohortMutation.isPending}
              data-testid="button-save-cohort"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                fontWeight: 700,
                border: "1px solid " + V3.border,
                background: V3.card,
                color: selectedIds.size > 0 ? "#D97706" : V3.textMuted,
                padding: "5px 14px",
                borderRadius: 6,
                cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
                opacity: selectedIds.size === 0 ? 0.5 : 1,
                marginRight: 6,
              }}
            >
              <Layers style={{ width: 14, height: 14 }} />
              Save as Cohort
            </button>
            <button
              type="button"
              onClick={handleBulkAction}
              disabled={selectedIds.size === 0 || skipMutation.isPending || unskipMutation.isPending}
              data-testid={isSkippedTab ? "button-bulk-restore" : "button-bulk-skip"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                fontWeight: 700,
                border: "1px solid " + V3.border,
                background: V3.card,
                color: selectedIds.size > 0 ? (isSkippedTab ? V3.good : V3.amber) : V3.textMuted,
                padding: "5px 14px",
                borderRadius: 6,
                cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
                opacity: selectedIds.size === 0 ? 0.5 : 1,
              }}
            >
              {(skipMutation.isPending || unskipMutation.isPending)
                ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                : <SkipForward style={{ width: 14, height: 14 }} />
              }
              {isSkippedTab ? "Restore" : "Skip"}
            </button>
          </div>
        )}

        {/* ── Table panel ──────────────────────────────────────────────── */}
        <div className="rounded-md shadow-sm" style={{ background: V3.card, padding: 16 }} data-testid={`tab-panel-${activeTab}`}>
          {listLoading ? (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <TabEmptyState
              copy={
                searchMode                 ? `No deliveries match "${debouncedSearch.trim()}" on any day.` :
                activeTab === "needsqa"    ? "Nothing waiting — all caught up." :
                activeTab === "inprogress" ? "No deliveries under senior review." :
                activeTab === "completed"  ? "No completed senior reviews for this date." :
                "Nothing skipped."
              }
              testid={searchMode ? "empty-search" : `empty-${activeTab}`}
            />
          ) : (
            <SeniorDeliveryTable
              rows={rows}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectAll={toggleSelectAll}
              searchMode={searchMode}
            />
          )}
        </div>
      </div>

      {/* ── Save as Cohort dialog ──────────────────────────────────────── */}
      <Dialog
        open={vipDialogOpen}
        onOpenChange={(open) => { if (!open && !vipImportMutation.isPending) setVipDialogOpen(false); }}
      >
        <DialogContent data-testid="dialog-vip-import" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>⭐ VIP Import</DialogTitle>
            <DialogDescription>
              Paste CookUnity's VIP list. Each line can be an Onfleet link, a short ID, or a customer
              name. Names are matched against the delivery date below (future dates work); links and
              IDs match directly. Flagged deliveries pin to the top of the list.
            </DialogDescription>
          </DialogHeader>

          {!vipReport ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 600, color: V3.textMuted }}>
                  Delivery date (for names)
                  <input
                    type="date"
                    value={vipDate}
                    onChange={(e) => setVipDate(e.target.value)}
                    disabled={vipImportMutation.isPending}
                    style={{ border: "1px solid " + V3.border, borderRadius: 6, padding: "5px 8px", fontSize: 13, fontFamily: Starling.fontBody, color: V3.text, background: "#fff" }}
                    data-testid="input-vip-date"
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 600, color: V3.textMuted }}>
                  Flag category
                  <select
                    value={vipCategory}
                    onChange={(e) => setVipCategory(isVipCategory(e.target.value) ? e.target.value : "")}
                    disabled={vipImportMutation.isPending}
                    style={{ border: "1px solid " + V3.border, borderRadius: 6, padding: "5px 8px", fontSize: 13, fontFamily: Starling.fontBody, color: vipCategory ? V3.text : V3.textMuted, background: "#fff", minWidth: 150 }}
                    data-testid="select-vip-category"
                  >
                    <option value="">Choose…</option>
                    {VIP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              <Textarea
                value={vipText}
                onChange={(e) => setVipText(e.target.value)}
                placeholder={"• jane doe | Order 12345678 | 30-day issue flag: 1 prior delivery order — undelivered.\nhttps://app.onfleet.com/dashboard#/manage?open=task&taskId=…\nac46e051"}
                rows={8}
                disabled={vipImportMutation.isPending}
                style={{ fontSize: 12, fontFamily: "ui-monospace, monospace" }}
                data-testid="input-vip-paste"
              />
            </div>
          ) : (
            <div style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 10 }} data-testid="vip-import-report">
              {vipReport.flagged.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.good, marginBottom: 4 }}>
                    ✓ Flagged ({vipReport.flagged.length})
                  </div>
                  {vipReport.flagged.map((f) => (
                    <div key={f.shortId} style={{ padding: "2px 0" }}>
                      {f.name} → <span style={{ fontFamily: "ui-monospace, monospace" }}>{f.shortId}</span>
                      {f.confidence === "loose" && <span style={{ color: V3.amber }}> (name-part match — verify)</span>}
                      {f.confidence === "id" && <span style={{ color: V3.textMuted }}> (by Onfleet ID)</span>}
                    </div>
                  ))}
                </div>
              )}
              {vipReport.unmatched.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.bad, marginBottom: 4 }}>
                    ✗ Unmatched ({vipReport.unmatched.length}) — no task with this name on {vipReport.date}
                  </div>
                  {vipReport.unmatched.map((n) => <div key={n} style={{ padding: "2px 0" }}>{n}</div>)}
                </div>
              )}
              {vipReport.notFound.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.bad, marginBottom: 4 }}>
                    ✗ Not in Onfleet ({vipReport.notFound.length}) — link or ID did not resolve
                  </div>
                  {vipReport.notFound.map((n, i) => (
                    <div key={`${n.ref}-${i}`} style={{ padding: "2px 0" }}>
                      {n.name !== n.ref ? `${n.name}: ` : ""}<span style={{ fontFamily: "ui-monospace, monospace" }}>{n.ref}</span>
                      {n.reason && <span style={{ color: V3.textMuted }}> — {n.reason}</span>}
                    </div>
                  ))}
                </div>
              )}
              {vipReport.rejected.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.bad, marginBottom: 4 }}>
                    ✗ Not a CookUnity delivery ({vipReport.rejected.length}) — link points at a different kind of task; not flagged
                  </div>
                  {vipReport.rejected.map((n, i) => (
                    <div key={`${n.ref}-${i}`} style={{ padding: "2px 0" }}>
                      {n.name !== n.ref ? `${n.name}: ` : ""}<span style={{ fontFamily: "ui-monospace, monospace" }}>{n.ref}</span>
                    </div>
                  ))}
                </div>
              )}
              {vipReport.ambiguous.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.amber, marginBottom: 4 }}>
                    ? Ambiguous ({vipReport.ambiguous.length}) — multiple tasks match; not flagged
                  </div>
                  {vipReport.ambiguous.map((a) => (
                    <div key={a.name} style={{ padding: "2px 0" }}>
                      {a.name}: {a.candidates.map((c) => c.shortId).join(", ")}
                    </div>
                  ))}
                </div>
              )}
              {vipReport.failed.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.bad, marginBottom: 4 }}>
                    ! Failed to flag ({vipReport.failed.length})
                  </div>
                  {vipReport.failed.map((f, i) => <div key={`${f.shortId}-${i}`} style={{ padding: "2px 0" }}>{f.name}: {f.error}</div>)}
                </div>
              )}
              {vipReport.skippedLines.length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, color: V3.textMuted, marginBottom: 4 }}>
                    – Skipped ({vipReport.skippedLines.length}) — didn't look like a name, link, or ID
                  </div>
                  {vipReport.skippedLines.map((l, i) => (
                    <div key={i} style={{ padding: "2px 0", color: V3.textMuted, fontFamily: "ui-monospace, monospace", fontSize: 11, whiteSpace: "pre-wrap" }}>{l}</div>
                  ))}
                </div>
              )}
              <div style={{ color: V3.textMuted, fontSize: 11 }}>
                Flagged as <b>{vipReport.category}</b>. Names matched against {vipReport.candidateCount} tasks
                for {vipReport.date}. Re-pasting later is safe — already-flagged deliveries are simply re-flagged.
              </div>
            </div>
          )}

          <DialogFooter>
            {!vipReport ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setVipDialogOpen(false)}
                  disabled={vipImportMutation.isPending}
                  data-testid="button-vip-cancel"
                >
                  Cancel
                </Button>
                <Button
                  disabled={vipImportMutation.isPending || !vipText.trim() || !vipCategory || !vipDate}
                  onClick={() => vipImportMutation.mutate()}
                  data-testid="button-vip-submit"
                >
                  {vipImportMutation.isPending
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Matching…</>
                    : <>⭐ Flag Deliveries</>}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setVipReport(null)}
                  data-testid="button-vip-again"
                >
                  Paste Again
                </Button>
                <Button onClick={() => { setVipDialogOpen(false); setVipText(""); setVipReport(null); }} data-testid="button-vip-done">
                  Done
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cohortDialogOpen}
        onOpenChange={(open) => { if (!open && !saveCohortMutation.isPending) setCohortDialogOpen(false); }}
      >
        <DialogContent data-testid="dialog-save-cohort">
          <DialogHeader>
            <DialogTitle>Save as Named Cohort</DialogTitle>
            <DialogDescription>
              Save {selectedIds.size} selected deliver{selectedIds.size !== 1 ? "ies" : "y"} as a
              reusable cohort for repeated baseline freezes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="cohort-name">
                Name <span className="text-destructive">*</span>
              </label>
              <Input
                id="cohort-name"
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
                placeholder="e.g. QATT 1 – June sample"
                disabled={saveCohortMutation.isPending}
                data-testid="input-cohort-name"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="cohort-description">
                Description <span className="text-muted-foreground text-xs">(optional)</span>
              </label>
              <Textarea
                id="cohort-description"
                value={cohortDescription}
                onChange={(e) => setCohortDescription(e.target.value)}
                placeholder="What's this cohort for?"
                rows={2}
                disabled={saveCohortMutation.isPending}
                data-testid="input-cohort-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cohort-qatt">
                  QATT <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <select
                  id="cohort-qatt"
                  value={cohortQatt}
                  onChange={(e) => setCohortQatt(e.target.value as "" | "1" | "3")}
                  disabled={saveCohortMutation.isPending}
                  data-testid="select-cohort-qatt"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">All</option>
                  <option value="1">QATT 1</option>
                  <option value="3">QATT 3</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="cohort-company">
                  Company <span className="text-muted-foreground text-xs">(optional)</span>
                </label>
                <Input
                  id="cohort-company"
                  value={cohortCompany}
                  onChange={(e) => setCohortCompany(e.target.value)}
                  placeholder="e.g. CookUnity"
                  disabled={saveCohortMutation.isPending}
                  data-testid="input-cohort-company"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCohortDialogOpen(false)}
              disabled={saveCohortMutation.isPending}
              data-testid="button-cohort-cancel"
            >
              Cancel
            </Button>
            <Button
              disabled={saveCohortMutation.isPending || !cohortName.trim()}
              onClick={() => saveCohortMutation.mutate()}
              data-testid="button-cohort-save"
            >
              {saveCohortMutation.isPending ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
              ) : (
                <><Layers className="h-4 w-4 mr-2" />Save {selectedIds.size} Deliver{selectedIds.size !== 1 ? "ies" : "y"}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete saved filter — confirm first (SPEC-2 §3.2) ─────────── */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="dialog-delete-filter">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the saved filter for you only. The list and its current filters are not changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-filter-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (deleteTarget) deleteSavedFilter(deleteTarget); }}
              data-testid="button-delete-filter-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
